package top.wcpe.allincore.dsl.compiler.semantic.`native`.methods

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.FunDecl
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.semantic.`native`.Arity
import top.wcpe.allincore.dsl.compiler.semantic.`native`.MethodDescriptor
import top.wcpe.allincore.dsl.compiler.semantic.`native`.ResolveCtx

/**
 * Stage 7a/7b：user-defined object 内 fun 的描述符。
 *
 * 由 [top.wcpe.allincore.dsl.compiler.semantic.`native`.ClassRegistry.registerUserObjects]
 * 在 pipeline 中按需创建。[resolve] 把 `ObjectName.method(args)` lower 为
 * `CallExpr(FunRefExpr("ObjectName#method"), args)`，runtime 通过 funMap key 查表执行。
 */
class UserObjectMethodDescriptor(
    val objectName: String,
    val funDecl: FunDecl,
) : MethodDescriptor {

    override val name: String = funDecl.name

    override val isSuspend: Boolean = funDecl.isSuspend

    /**
     * Stage 7b：按 defaults 感知 arity 范围。
     *
     * 若所有形参均无默认值 → Fixed(total)；否则 → Range(required, total)。
     */
    override val arity: Arity = run {
        val total = funDecl.params.size
        val required = if (funDecl.defaults.isEmpty()) total else funDecl.defaults.count { it == null }
        if (required == total) Arity.Fixed(total) else Arity.Range(required, total)
    }

    /**
     * Stage 7b：把 MemberCall lower 为 CallExpr(FunRefExpr("ObjectName#method"), args)。
     *
     * 防御校验失败时返回 null（保留原 MemberCall），由上层决定是否报错。
     */
    override fun resolve(call: ExprAst.MemberCall, diag: DiagnosticCollector, ctx: ResolveCtx): ExprAst? {
        // 防御：target 必须是 IdentRef(objectName)
        val target = call.target
        if (target !is ExprAst.IdentRef || target.name != objectName) return null
        // 防御：方法名匹配
        if (call.method != funDecl.name) return null

        // 复用统一的 arity / 命名实参校验，避免与 NativeClassResolverPass 的嵌套 lower 路径分歧
        if (!validateCallShape(funDecl, call)) return null

        // 生成 lower 后节点：CallExpr(FunRefExpr("ObjectName#methodName"), args, namedArgs)
        return ExprAst.CallExpr(
            callee = ExprAst.FunRefExpr(funRefKey(objectName, funDecl.name), call.position),
            args = call.args,
            position = call.position,
            namedArgs = call.namedArgs,
        )
    }

    companion object {

        /**
         * user object FunRefExpr.name 与 ScreenEmitter funMap key 的成员分隔符。
         *
         * 写在 [UserObjectMethodDescriptor.funRefKey] 的产生侧，并由
         * [top.wcpe.allincore.dsl.compiler.codegen.ScreenEmitter] 与
         * [top.wcpe.allincore.dsl.compiler.semantic.ClassBodyChecker] 共享，
         * 避免分隔符在多个文件中以字面量散落。
         */
        const val FUN_REF_SEPARATOR: String = "#"

        /**
         * 把 user object 名 + 方法名格式化为 funMap / FunRefExpr.name 的 key。
         *
         * 与 [top.wcpe.allincore.dsl.compiler.codegen.ScreenEmitter] 写入
         * `UiIr.functions` 的 key 共享同一约定，避免在编译期与 codegen 端散落
         * 字符串拼接导致漂移。
         */
        fun funRefKey(objectName: String, methodName: String): String =
            "${objectName}${FUN_REF_SEPARATOR}${methodName}"

        /**
         * 共享的 user object fun 调用形状校验。
         *
         * 既被本描述符的 [resolve]（顶层 user object 调用）使用，也被
         * [top.wcpe.allincore.dsl.compiler.pipeline.NativeClassResolverPass.resolveNestedMemberCall]
         * （Stage 8 嵌套 user object 调用）复用，确保两处路径对 arity / 默认值 /
         * 命名实参合法性保持一致行为。
         *
         * @return 通过返回 true；任一项不合规返回 false
         */
        fun validateCallShape(funDecl: FunDecl, call: ExprAst.MemberCall): Boolean {
            val totalArgs = call.args.size + call.namedArgs.size
            val totalParams = funDecl.params.size
            val requiredParams = if (funDecl.defaults.isEmpty()) totalParams
            else funDecl.defaults.count { it == null }
            if (totalArgs < requiredParams || totalArgs > totalParams) return false
            val paramNames = funDecl.params.toSet()
            for (na in call.namedArgs) {
                if (na.name !in paramNames) return false
            }
            return true
        }
    }
}
